import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Text, Link, Image
} from 'components/ui';
import { fontSizes, space } from 'style/config';
import localeUtils from 'utils/LanguageLocale';
import urlUtils from 'utils/Url';
import FooterEN from 'components/Footer/locales/Footer_en_US';
import FooterFR from 'components/Footer/locales/Footer_fr_CA';

// Non-deprecated localisation API (UA-2307): getLocalisedDataFromResource with
// the imported EN/FR resource modules, not the deprecated getLocaleResourceFile.
const getText = localeUtils.getLocalisedDataFromResource(FooterEN, FooterFR);

/**
 * LegalFooter — the bottom legal bar ONLY of the global footer, extracted for
 * reuse on auth pages (SIM-2 / ACS-4126, Figma node 270-84077).
 *
 * Renders the copyright line, the legal links (Privacy Policy, Terms of Use,
 * Accessibility, Sitemap, Your Privacy Choices/Clarip) and the phone number —
 * left-aligned, white on black. Deliberately omits the footer's nav columns,
 * email/SMS signup, social links, region switcher, Medallia and Braze (those
 * belong to the full `components/Footer/Footer.ctrlr`). Reuses the existing
 * `components/Footer/locales` strings so copy stays in one place.
 */
const LegalFooter = () => {
    const isCanada = localeUtils.isCanada();
    const showClaripLink = !isCanada || (isCanada && Sephora.configurationSettings.isClaripPrivacyEnabled);

    return (
        <Box
            is='footer'
            backgroundColor='black'
            color='white'
            fontSize={fontSizes.sm}
            paddingY={5}
            paddingX={['16px', null, SIDE_GUTTER_DESKTOP]}
            css={styles.root}
        >
            {/* Fixed gutter (16px mobile → 96px desktop) so the footer's left
                inset matches the SignIn header logo (Figma desktop px-96). NOT a
                centered Container (that grows the gutter on wide viewports). */}
            <Text
                is='p'
                marginBottom={1}
                children={`© ${new Date().getFullYear()} ${getText('allRightReserved')}`}
            />
            <Text
                is='p'
                marginX={-2}
                marginY={1}
            >
                <Link
                    href={urlUtils.getLink('/beauty/privacy-policy')}
                    paddingY={1}
                    paddingX={2}
                    children={getText('privacyPolicy')}
                />
                <Link
                    href={urlUtils.getLink('/beauty/terms-of-use')}
                    paddingY={1}
                    paddingX={2}
                    children={getText('termsOfUse')}
                />
                <Link
                    href='/beauty/accessibility'
                    paddingY={1}
                    paddingX={2}
                    children={getText('accessibility')}
                />
                <Link
                    href='/sitemap/departments/'
                    paddingY={1}
                    paddingX={2}
                    children={getText('siteMap')}
                />
                {showClaripLink && (
                    <Link
                        is='button'
                        paddingY={1}
                        paddingX={2}
                        id={isCanada ? 'consent-management-tool' : 'clarip-do-not-sell-link'}
                        children={
                            <>
                                {isCanada ? getText('yourPrivacyChoicesCanada') : getText('yourPrivacyChoices')}
                                <Image
                                    alt=''
                                    marginLeft='.25em'
                                    src='/img/ufe/icons/opt-out.svg'
                                    width={30}
                                    height={14}
                                    css={{ verticalAlign: 'text-bottom' }}
                                />
                            </>
                        }
                    />
                )}
            </Text>
            <Text
                is='p'
                children='1-877-737-4672'
            />
        </Box>
    );
};

// Figma desktop footer gutter (px-96); mobile uses a 16px gutter.
const SIDE_GUTTER_DESKTOP = '96px';

const styles = {
    root: {
        marginTop: space[7],
        textAlign: 'left'
    }
};

export default wrapFunctionalComponent(LegalFooter, 'LegalFooter');
